<template>
  <v-dialog :value="value" max-width="760" @input="handleInput">
    <v-card>
      <v-card-title class="pb-1">Geçiş Kararı</v-card-title>
      <v-card-subtitle>
        Satır bazlı ürün ve fiyat kararını burada belirleyin.
      </v-card-subtitle>

      <v-card-text v-if="row">
        <v-alert
          v-if="
            row.current_product_options &&
            row.current_product_options.length > 1
          "
          type="warning"
          outlined
          dense
          class="mb-4"
        >
          Bu müşteride birden fazla aktif aylık ürün bulundu. Geçiş için tek bir
          ürün ve tek bir fiyat seçmelisiniz.
        </v-alert>

        <v-row dense class="mb-3">
          <v-col cols="12" md="6">
            <v-sheet outlined rounded class="pa-3">
              <div class="text-caption text--secondary">Müşteri</div>
              <div class="font-weight-medium">{{ row.customer_name }}</div>
              <div class="text-caption text--secondary">
                {{ row.cluster_label || "-" }}
              </div>
            </v-sheet>
          </v-col>

          <v-col cols="12" md="6">
            <v-sheet outlined rounded class="pa-3">
              <div class="text-caption text--secondary">Planlanan Dönem</div>
              <div class="font-weight-medium">{{ periodLabel }}</div>
              <div class="text-caption text--secondary">
                {{ row.billing_type || "-" }}
              </div>
            </v-sheet>
          </v-col>
        </v-row>

        <v-card outlined class="mb-4">
          <v-card-title class="pb-1 text-subtitle-2">
            Mevcut Aktif Ürünler
          </v-card-title>
          <v-card-text class="pb-2">
            <v-chip
              v-for="option in currentProductOptions"
              :key="`${option.product_row_id}-${option.product_id}`"
              small
              outlined
              color="primary"
              class="mr-2 mb-2"
            >
              {{ option.label }} • {{ option.period_label }}
            </v-chip>

            <div
              v-if="!currentProductOptions.length"
              class="text-caption text--secondary"
            >
              Aktif ürün özeti bulunamadı.
            </div>
          </v-card-text>
        </v-card>

        <v-form ref="decisionForm">
          <v-row dense>
            <v-col cols="12">
              <v-select
                v-model="form.product_id"
                :items="productItems"
                item-text="display_label"
                item-value="id"
                label="Yeni Ürün"
                outlined
                dense
                :rules="[requiredRule]"
                @change="handleProductChange"
              />
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model.number="form.amount"
                label="Yeni Birim Tutar"
                outlined
                dense
                type="number"
                step="0.01"
                min="0"
                prefix="₺"
                :rules="[requiredAmountRule]"
              />
            </v-col>
          </v-row>
        </v-form>

        <div class="text-caption text--secondary">
          Seçtiğiniz fiyat müşteri ürün kaydına yazılacak birim fiyatı temsil
          eder. Fatura preview'i bu değer üzerinden üretilecek.
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="handleInput(false)">Kapat</v-btn>
        <v-btn v-if="existingOverride" text color="warning" @click="emitReset">
          Varsayılana Dön
        </v-btn>
        <v-btn color="primary" @click="emitSave">Kararı Kaydet</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "PortfolioYearEndTransitionDecisionDialog",
  props: {
    value: {
      type: Boolean,
      required: false,
      default: false,
    },
    row: {
      type: Object,
      required: false,
      default: null,
    },
    standardProducts: {
      type: Array,
      required: false,
      default: () => [],
    },
    existingOverride: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      form: {
        product_id: null,
        amount: null,
      },
    };
  },
  computed: {
    currentProductOptions() {
      return this.row?.current_product_options || [];
    },
    productItems() {
      return (this.standardProducts || []).map((item) => ({
        ...item,
        display_label: `${item.name} • ${this.formatCurrency(
          item.monthly_price_per_house
        )}/konut`,
      }));
    },
    effectiveDateLabel() {
      const effectiveDate = this.row?.effective_date;

      if (!effectiveDate) {
        return "-";
      }

      const [year, month, day] = String(effectiveDate).split("-");

      if (!year || !month || !day) {
        return effectiveDate;
      }

      return `${day}.${month}.${year}`;
    },
    periodLabel() {
      if (this.row?.next_period_label) {
        return this.row.next_period_label;
      }

      return this.effectiveDateLabel;
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.initializeForm();
      }
    },
    row() {
      if (this.value) {
        this.initializeForm();
      }
    },
  },
  methods: {
    handleInput(value) {
      this.$emit("input", value);
    },
    requiredRule(value) {
      return !!value || "Ürün seçmelisiniz.";
    },
    requiredAmountRule(value) {
      if (value === null || value === "" || Number.isNaN(Number(value))) {
        return "Tutar girmelisiniz.";
      }

      return Number(value) >= 0 || "Tutar negatif olamaz.";
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(value || 0));
    },
    getStandardProduct(productId) {
      return (this.standardProducts || []).find(
        (item) => Number(item.id) === Number(productId)
      );
    },
    getDefaultAmountForProduct(productId) {
      if (Number(productId) === Number(this.row?.current_product_id || 0)) {
        return this.row?.current_product_amount ?? null;
      }

      return (
        this.getStandardProduct(productId)?.monthly_price_per_house ?? null
      );
    },
    resolveInitialProductId() {
      if (this.existingOverride?.product_id) {
        return Number(this.existingOverride.product_id);
      }

      if (this.row?.current_product_id) {
        return Number(this.row.current_product_id);
      }

      if (this.currentProductOptions.length === 1) {
        return Number(this.currentProductOptions[0].product_id);
      }

      return null;
    },
    initializeForm() {
      const initialProductId = this.resolveInitialProductId();

      this.form.product_id = initialProductId;
      this.form.amount =
        this.existingOverride?.amount ??
        this.getDefaultAmountForProduct(initialProductId);
    },
    handleProductChange(value) {
      this.form.amount = this.getDefaultAmountForProduct(value);
    },
    emitSave() {
      if (!this.row) {
        return;
      }

      if (!this.$refs.decisionForm.validate()) {
        return;
      }

      this.$emit("save", {
        row_key: this.row.row_key,
        product_id: Number(this.form.product_id),
        amount: Number(this.form.amount),
      });
    },
    emitReset() {
      if (!this.row) {
        return;
      }

      this.$emit("reset", this.row.row_key);
      this.handleInput(false);
    },
  },
};
</script>
